import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useUser} from "./UserContext";
import axios from "axios";
import styles from "../designs/ProfilePage.module.css";
import {isDemoModeEnabled} from "../utils/demoAccess";

const GENERATION_SOUND_ENABLED_KEY = "grachGenerationSoundEnabled";
const GENERATION_SOUND_VOLUME_KEY = "grachGenerationSoundVolume";
const DEFAULT_GENERATION_SOUND_VOLUME = 0.35;

const readGenerationSoundVolume = () => {
    const raw = localStorage.getItem(GENERATION_SOUND_VOLUME_KEY);
    if (raw === null || raw === "") return DEFAULT_GENERATION_SOUND_VOLUME;
    const saved = Number(raw);
    return Number.isFinite(saved) ? Math.max(0, Math.min(1, saved)) : DEFAULT_GENERATION_SOUND_VOLUME;
};

export default function ProfilePage() {
    const {user, setUser} = useUser();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [favoriteTeams, setFavoriteTeams] = useState([]);
    const [recentBets, setRecentBets] = useState([]);
    const [pointsInfo, setPointsInfo] = useState({balance: 0, ledger: [], purchases: []});
    const [copyStatus, setCopyStatus] = useState("");
    const [hapticsEnabled, setHapticsEnabled] = useState(
        () => localStorage.getItem("grachHapticsEnabled") !== "false"
    );
    const [generationSoundEnabled, setGenerationSoundEnabled] = useState(
        () => localStorage.getItem(GENERATION_SOUND_ENABLED_KEY) !== "false"
    );
    const [generationSoundVolume, setGenerationSoundVolume] = useState(readGenerationSoundVolume);
    const navigate = useNavigate();

    useEffect(() => {
        if (isDemoModeEnabled()) {
            navigate("/access-request", {replace: true});
            return;
        }

        const cachedUserRaw = sessionStorage.getItem("telegramUser") || localStorage.getItem("telegramUser");
        let activeUser = user;

        if (!activeUser && cachedUserRaw) {
            try {
                activeUser = JSON.parse(cachedUserRaw);
                setUser(activeUser);
            } catch {
                activeUser = null;
            }
        }

        if (!activeUser?.telegram_id) {
            setError("Користувач не знайдений. Відкрийте профіль через Telegram.");
            setLoading(false);
            return;
        }

        const loadProfile = async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || "/api";
                const telegramId = activeUser.telegram_id;
                const [userResult, favoritesResult, betsResult] = await Promise.allSettled([
                    axios.get(`${API_BASE}/user/${telegramId}`),
                    axios.get(`${API_BASE}/user/${telegramId}/favorite-teams`),
                    axios.get(`${API_BASE}/user/${telegramId}/bets`),
                ]);
                const [pointsResult] = await Promise.allSettled([
                    axios.get(`${API_BASE}/user/${telegramId}/points`),
                ]);

                if (userResult.status !== "fulfilled") {
                    throw userResult.reason;
                }

                const freshUser = userResult.value.data.user;
                setUser(freshUser);
                sessionStorage.setItem("telegramUser", JSON.stringify(freshUser));
                localStorage.setItem("telegramUser", JSON.stringify(freshUser));

                if (favoritesResult.status === "fulfilled") {
                    setFavoriteTeams(favoritesResult.value.data.favorite_teams || []);
                }
                if (betsResult.status === "fulfilled") {
                    const bets = Array.isArray(betsResult.value.data.bets) ? betsResult.value.data.bets : [];
                    setRecentBets([...bets].reverse().slice(0, 3));
                }
                if (pointsResult.status === "fulfilled") {
                    setPointsInfo({
                        balance: pointsResult.value.data.balance || 0,
                        ledger: pointsResult.value.data.ledger || [],
                        purchases: pointsResult.value.data.purchases || [],
                    });
                }
            } catch (err) {
                console.error("Помилка при отриманні даних профілю:", err);
                setError("Не вдалося завантажити дані профілю");
            } finally {
                setLoading(false);
            }
        };

        loadProfile();
    }, [user?.telegram_id, setUser]);

    const displayName = user?.first_name || user?.username || "Грач";
    const initials = useMemo(() => {
        const source = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.username || "G";
        return source.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
    }, [user]);

    const accountDays = useMemo(() => {
        if (!user?.created_at) return 1;
        const created = new Date(user.created_at);
        if (Number.isNaN(created.getTime())) return 1;
        return Math.max(1, Math.ceil((Date.now() - created.getTime()) / 86400000));
    }, [user?.created_at]);

    const openTelegramLink = (url) => {
        if (window.Telegram?.WebApp?.openTelegramLink) {
            window.Telegram.WebApp.openTelegramLink(url);
        } else {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const copyTelegramId = async () => {
        try {
            await navigator.clipboard.writeText(String(user.telegram_id));
            setCopyStatus("Скопійовано");
            window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
        } catch {
            setCopyStatus("Не вдалося");
        }
        window.setTimeout(() => setCopyStatus(""), 1600);
    };

    const toggleHaptics = () => {
        const next = !hapticsEnabled;
        setHapticsEnabled(next);
        localStorage.setItem("grachHapticsEnabled", String(next));
        if (next) window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.("light");
    };

    const toggleGenerationSound = () => {
        const next = !generationSoundEnabled;
        setGenerationSoundEnabled(next);
        localStorage.setItem(GENERATION_SOUND_ENABLED_KEY, String(next));
        window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
    };

    const updateGenerationSoundVolume = (event) => {
        const rawValue = Number(event.target.value);
        const next = Number.isFinite(rawValue)
            ? Math.max(0, Math.min(1, rawValue / 100))
            : DEFAULT_GENERATION_SOUND_VOLUME;
        setGenerationSoundVolume(next);
        localStorage.setItem(GENERATION_SOUND_VOLUME_KEY, String(next));
    };

    const getBetTitle = (bet) => {
        if (bet.match) return String(bet.match).replace(" vs ", " — ");
        if (bet.home_team || bet.away_team) return `${bet.home_team || "Команда"} — ${bet.away_team || "Команда"}`;
        return "Матч";
    };

    if (loading) {
        return <div className={styles.statePage}><div className={styles.loader}/><span>Завантажуємо профіль</span></div>;
    }
    if (error) {
        return <div className={styles.statePage}><span className={styles.errorText}>{error}</span></div>;
    }
    if (!user) return null;

    return (
        <div className={styles.page}>
            <div className={styles.ambientBackground}/>
            <main className={styles.profileShell}>
                <header className={styles.topBar}>
                    <button onClick={() => navigate(-1)} className={styles.backButton}>← Назад</button>
                    <div className={styles.brandMark}><span>GRACH</span><small>MEMBER PROFILE</small></div>
                </header>

                <section className={styles.memberCard}>
                    <div className={styles.memberCardGlow}/>
                    <div className={styles.avatarFrame}>
                        {user.photo_url ? (
                            <img src={user.photo_url} alt={displayName} className={styles.avatar}/>
                        ) : (
                            <div className={styles.avatarPlaceholder}>{initials}</div>
                        )}
                        <span className={styles.onlineDot}/>
                    </div>

                    <div className={styles.memberIdentity}>
                        <div className={styles.memberKicker}>Учасник сім’ї</div>
                        <h1>{displayName}</h1>
                        <div className={styles.memberUsername}>@{user.username || "grach_member"}</div>
                    </div>

                    <div className={styles.accessBadge}>
                        <span/>{user.has_access ? "Доступ активний" : "Без доступу"}
                    </div>
                </section>

                <section className={styles.statsRow}>
                    <div className={styles.statCard}><strong>{user.total_bets ?? recentBets.length}</strong><span>Ставок</span></div>
                    <div className={styles.statCard}><strong>{favoriteTeams.length}</strong><span>Обраних</span></div>
                    <div className={styles.statCard}><strong>{accountDays}</strong><span>Днів</span></div>
                    <div className={styles.statCard}><strong>{pointsInfo.balance}</strong><span>Балів</span></div>
                </section>

                <section className={styles.quickActions}>
                    <button onClick={() => navigate("/football")}><span>⚽</span><strong>Матчі</strong><small>Відкрити лінію</small></button>
                    <button onClick={() => navigate("/history")}><span>↺</span><strong>Історія</strong><small>Мої ставки</small></button>
                    <button onClick={() => navigate("/shop")}><span>＋</span><strong>Магазин</strong><small>{pointsInfo.balance} балів</small></button>
                    <button onClick={() => openTelegramLink("https://t.me/+Z4Lm60-lJZFhZTVi")}><span>↗</span><strong>Канал</strong><small>Грач Футбол</small></button>
                    <button onClick={() => openTelegramLink("https://t.me/grach_admin")}><span>♔</span><strong>Підтримка</strong><small>@grach_admin</small></button>
                </section>

                <div className={styles.contentGrid}>
                    <section className={styles.panel}>
                        <div className={styles.panelHeader}><div><small>MY CLUBS</small><h2>Обрані команди</h2></div><span>{favoriteTeams.length}</span></div>
                        {favoriteTeams.length ? (
                            <div className={styles.favoriteList}>
                                {favoriteTeams.map((team) => <button key={team} onClick={() => navigate("/football")}><i>★</i>{team}<span>→</span></button>)}
                            </div>
                        ) : (
                            <div className={styles.emptyState}>Натискай ☆ на сторінці матчу — команда з’явиться тут.</div>
                        )}
                    </section>

                    <section className={styles.panel}>
                        <div className={styles.panelHeader}><div><small>RECENT ACTIVITY</small><h2>Останні ставки</h2></div><button onClick={() => navigate("/history")}>Усі</button></div>
                        {recentBets.length ? (
                            <div className={styles.recentList}>
                                {recentBets.map((bet, index) => (
                                    <button key={`${getBetTitle(bet)}-${index}`} onClick={() => navigate("/history")}>
                                        <span className={styles.betIndex}>{String(index + 1).padStart(2, "0")}</span>
                                        <span className={styles.betInfo}><strong>{getBetTitle(bet)}</strong><small>{bet.date || bet.match_date || "Збережена ставка"}</small></span>
                                        <span className={styles.betArrow}>→</span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.emptyState}>Твоя перша згенерована ставка з’явиться тут.</div>
                        )}
                    </section>
                </div>

                <section className={styles.settingsPanel}>
                    <div className={styles.settingsTitle}><small>APP SETTINGS</small><h2>Налаштування</h2></div>
                    <button className={styles.settingRow} onClick={toggleHaptics}>
                        <span className={styles.settingIcon}>〰</span>
                        <span><strong>Вібрація після генерації</strong><small>Haptic-відгук, коли ставка готова</small></span>
                        <i className={`${styles.toggle} ${hapticsEnabled ? styles.toggleActive : ""}`}><b/></i>
                    </button>
                    <button className={styles.settingRow} onClick={toggleGenerationSound}>
                        <span className={styles.settingIcon}>♪</span>
                        <span><strong>Звук під час генерації</strong><small>{generationSoundEnabled ? "Увімкнено" : "Вимкнено"}</small></span>
                        <i className={`${styles.toggle} ${generationSoundEnabled ? styles.toggleActive : ""}`}><b/></i>
                    </button>
                    <div className={`${styles.settingRow} ${styles.volumeRow}`}>
                        <span className={styles.settingIcon}>VOL</span>
                        <span>
                            <strong>Гучність звуку</strong>
                            <small>{Math.round(generationSoundVolume * 100)}%</small>
                        </span>
                        <input
                            className={styles.volumeSlider}
                            type="range"
                            min="0"
                            max="100"
                            value={Math.round(generationSoundVolume * 100)}
                            onChange={updateGenerationSoundVolume}
                            disabled={!generationSoundEnabled}
                            aria-label="Гучність звуку генерації"
                        />
                    </div>
                    <button className={styles.settingRow} onClick={copyTelegramId}>
                        <span className={styles.settingIcon}>#</span>
                        <span><strong>Telegram ID</strong><small>{copyStatus || user.telegram_id}</small></span>
                        <em>Копіювати</em>
                    </button>
                </section>

                <button className={styles.partnerButton} onClick={() => window.open("https://lkan.pro/e47773", "_blank", "noopener,noreferrer")}>Офіційний партнер <strong>1win</strong><span>→</span></button>
            </main>
        </div>
    );
}
